export interface SourceNavigationTarget {
  id?: string;
  fileId?: string;
  documentId?: string;
  sourceId?: string;
  title?: string;
  fileName?: string;
  excerpt?: string;
  snippet?: string;
  pageNumber?: number | string | null;
  page_number?: number | string | null;
}

const SEARCH_PHRASE_PATTERNS: RegExp[] = [
  /(.+?)\s*(?:に関して|について|に対して)\s*(?:検索|調べ|教え|説明し|まとめ)て(?:ください)?[。.!?！？]*$/,
  /(.+?)\s*(?:を|で|の)\s*(?:検索|調べ|教え)て(?:ください)?[。.!?！？]*$/,
  /(.+?)\s*(?:って何|とは何|とは|って)(?:\?|？)?$/,
  /(.+?)\s*を\s*(?:検索|調べて|教えて)(?:ください)?[。.!?！？]*$/,
  /^(?:please\s+)?tell\s+me\s+about\s+(.+?)[。.!?！？]*$/i,
  /^(?:please\s+)?(?:search\s+(?:for\s+)?|find\s+)(.+?)[。.!?！？]*$/i,
];

const IDENTIFIER_PATTERN = /[A-Za-z][A-Za-z0-9_-]*\d+[A-Za-z0-9_-]*|[A-Z]{2,}[A-Za-z0-9_-]*/g;
const JAPANESE_ANCHOR_PATTERN = /[一-龯々〆ヵヶぁ-んァ-ヴー・]{4,}/g;
const PAGE_MARKER_PATTERNS: Array<{ pattern: RegExp; totalGroup?: number }> = [
  { pattern: /-{2,}\s*Page\s+(\d+)\s*-{2,}/i },
  { pattern: /-{2,}\s*ページ\s*(\d+)\s*-{2,}/ },
];
const PRINTED_PAGE_MARKER_PATTERN = /(?:^|[^\d])(\d{1,4})\s*\/\s*(\d{1,4})(?:[^\d]|$)/;

const normalizeToken = (value: string) => value.toLowerCase();

const parsePositivePage = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const page = typeof value === 'number' ? value : parseInt(value, 10);
  return Number.isInteger(page) && page > 0 ? page : null;
};

const sourceText = (source?: SourceNavigationTarget): string => {
  if (!source) return '';
  return [source.excerpt, source.snippet, source.title, source.fileName]
    .filter(Boolean)
    .join(' ');
};

const sourceContentText = (source?: SourceNavigationTarget): string => {
  if (!source) return '';
  return [source.excerpt, source.snippet].filter(Boolean).join(' ');
};

const findPageMarker = (source?: SourceNavigationTarget): number | null => {
  const text = sourceContentText(source);
  for (const { pattern, totalGroup } of PAGE_MARKER_PATTERNS) {
    const match = text.match(pattern);
    const page = parsePositivePage(match?.[1]);
    const total = totalGroup ? parsePositivePage(match?.[totalGroup]) : null;
    if (totalGroup && (!total || !page || page > total || total > 1000)) {
      continue;
    }
    if (page) return page;
  }
  return null;
};

const findPrintedPageMarker = (source?: SourceNavigationTarget): number | null => {
  const match = sourceContentText(source).match(PRINTED_PAGE_MARKER_PATTERN);
  const page = parsePositivePage(match?.[1]);
  const total = parsePositivePage(match?.[2]);
  if (!page || !total || page > total || total > 1000) return null;
  return page;
};

const findSharedIdentifier = (query: string, source?: SourceNavigationTarget): string => {
  const sourceIdentifiers = new Set(
    (sourceText(source).match(IDENTIFIER_PATTERN) || []).map(normalizeToken),
  );
  if (sourceIdentifiers.size === 0) return '';

  for (const token of query.match(IDENTIFIER_PATTERN) || []) {
    if (sourceIdentifiers.has(normalizeToken(token))) {
      return token;
    }
  }
  return '';
};

const escapeRegExp = (value: string): string => (
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
);

const findSourceAnchorNearIdentifier = (
  query: string,
  source?: SourceNavigationTarget,
): string => {
  const identifier = findSharedIdentifier(query, source);
  if (!identifier) return '';

  const text = sourceContentText(source).replace(/\s+/g, ' ').trim();
  if (!text) return '';

  const compactText = text.replace(/\s+/g, '');
  const compactIdentifier = identifier.replace(/\s+/g, '');
  const preferredExactAnchors = [
    '添付資料として以下',
  ];
  for (const anchor of preferredExactAnchors) {
    if (compactText.includes(anchor)) {
      return anchor;
    }
  }

  const preferredSuffixes = [
    '資料には以下',
    '資料に対する質疑応答',
    'を開催するに当たり',
  ];
  for (const suffix of preferredSuffixes) {
    if (compactText.includes(`${compactIdentifier}${suffix}`)) {
      return suffix;
    }
  }

  const match = text.match(new RegExp(escapeRegExp(identifier), 'i'));
  if (!match || match.index === undefined) return '';

  const before = text.slice(Math.max(0, match.index - 48), match.index);
  const candidates = before.match(JAPANESE_ANCHOR_PATTERN) || [];
  return candidates[candidates.length - 1] || '';
};

export const extractSearchKeyword = (
  query: string,
  source?: SourceNavigationTarget,
): string => {
  const trimmed = query.trim();
  if (!trimmed) return '';

  const sharedIdentifier = findSharedIdentifier(trimmed, source);
  if (sharedIdentifier) return sharedIdentifier;

  for (const pattern of SEARCH_PHRASE_PATTERNS) {
    const match = trimmed.match(pattern);
    if (match?.[1]) {
      return match[1].trim();
    }
  }

  return trimmed;
};

const extractSourceAnchoredKeyword = (
  query: string,
  source?: SourceNavigationTarget,
): string => (
  findSourceAnchorNearIdentifier(query, source) || extractSearchKeyword(query, source)
);

export const resolveInitialPage = (
  pageParam: string | null,
  fallbackPage = 1,
): number => {
  if (!pageParam) {
    return parsePositivePage(fallbackPage) || 1;
  }
  return parsePositivePage(pageParam) || 1;
};

export const getSourceDocumentId = (source: SourceNavigationTarget): string => (
  source.fileId || source.documentId || source.sourceId || source.id || ''
);

export const buildSourceDocumentUrl = (
  source: SourceNavigationTarget,
  query = '',
): string => {
  const documentId = getSourceDocumentId(source);
  if (!documentId) return '';

  const params = new URLSearchParams();
  const page = parsePositivePage(source.pageNumber ?? source.page_number) ?? findPageMarker(source);
  if (page) {
    params.set('page', String(page));
  } else {
    const approximatePage = findPrintedPageMarker(source);
    if (approximatePage) {
      params.set('approxPage', String(approximatePage));
    }
  }

  const keyword = page
    ? extractSearchKeyword(query, source)
    : extractSourceAnchoredKeyword(query, source);
  if (keyword) {
    params.set('highlight', keyword);
  }

  const queryString = params.toString();
  return queryString
    ? `/documents/${documentId}?${queryString}`
    : `/documents/${documentId}`;
};
